import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import BalanceRounded from '@mui/icons-material/BalanceRounded';
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import SavingsOutlined from '@mui/icons-material/SavingsOutlined';
import ShowChartRounded from '@mui/icons-material/ShowChartRounded';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import WarehouseOutlined from '@mui/icons-material/WarehouseOutlined';
import { Box, Card, CardActionArea, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../../auth/AuthProvider';
import { PageHeader } from '../../../components/states';
import { IconBadge, NoAccess, type Tone } from '../components/ui';
import { canOpen, groupedReports, type ReportGroup } from '../lib/reportCatalog';

const ICONS: Record<string, ReactNode> = {
  'sales-by-period': <ShowChartRounded />,
  'sales-by-customer': <PeopleAltOutlined />,
  'sales-by-product': <Inventory2Outlined />,
  'best-sellers': <EmojiEventsOutlined />,
  'purchases-by-period': <LocalShippingOutlined />,
  'purchases-by-supplier': <StorefrontOutlined />,
  'purchases-by-product': <Inventory2Outlined />,
  'gross-profit-by-period': <SavingsOutlined />,
  'gross-profit-by-product': <SavingsOutlined />,
  'stock-on-hand': <WarehouseOutlined />,
  'low-stock': <ReportProblemOutlined />,
  'stock-valuation': <Inventory2Outlined />,
  'receivables-aging': <HourglassBottomRounded />,
  'payables-aging': <HourglassBottomRounded />,
  payments: <PaymentsOutlined />,
  'cash-movement': <SwapVertRounded />,
  'profit-and-loss': <TableChartOutlined />,
  'balance-sheet': <AccountBalanceOutlined />,
  'trial-balance': <BalanceRounded />,
};

const GROUP_TONE: Record<ReportGroup, Tone> = {
  sales: 'primary',
  purchases: 'info',
  profit: 'success',
  stock: 'warning',
  parties: 'primary',
  financial: 'info',
};

/** The reports hub: every report grouped by topic, locked ones shown greyed with a reason. */
export function ReportsHub() {
  const { can } = useAuth();
  const groups = groupedReports();
  const anyAllowed = groups.some((g) => g.reports.some((r) => canOpen(r, can)));
  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Sales, purchases, stock and the books — filter, chart, print or export to Excel or PDF."
      />
      {!anyAllowed ? (
        <NoAccess what="reports" />
      ) : (
        <Stack spacing={4}>
          {groups.map(({ group, reports }) => (
            <Box component="section" key={group.key} aria-labelledby={`grp-${group.key}`}>
              <Typography id={`grp-${group.key}`} variant="h5">
                {group.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>
                {group.subtitle}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: 'minmax(0,1fr)',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                    xl: 'repeat(4, 1fr)',
                  },
                }}
              >
                {reports.map((r) => {
                  const allowed = canOpen(r, can);
                  const body = (
                    <Stack direction="row" spacing={1.75} sx={{ p: 2.25, alignItems: 'center' }}>
                      <IconBadge
                        icon={ICONS[r.slug] ?? <ShowChartRounded />}
                        tone={allowed ? GROUP_TONE[r.group] : 'neutral'}
                        size={44}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 650, lineHeight: 1.3 }}>
                          {r.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {r.description}
                        </Typography>
                      </Box>
                      {allowed ? (
                        <ArrowForwardRounded
                          className="arrow"
                          fontSize="small"
                          sx={{
                            color: 'text.disabled',
                            transition: 'transform 160ms ease, color 160ms ease',
                          }}
                        />
                      ) : (
                        <LockOutlined fontSize="small" sx={{ color: 'text.disabled' }} />
                      )}
                    </Stack>
                  );
                  return (
                    <Card
                      key={r.slug}
                      sx={{
                        opacity: allowed ? 1 : 0.6,
                        transition: 'box-shadow 160ms ease, transform 160ms ease',
                        '&:hover': allowed
                          ? {
                              boxShadow: '0 12px 28px -8px rgba(16,24,40,0.14)',
                              transform: 'translateY(-1px)',
                              '& .arrow': { transform: 'translateX(3px)', color: 'primary.main' },
                            }
                          : undefined,
                        '@media (prefers-reduced-motion: reduce)': {
                          transition: 'none',
                          '&:hover': { transform: 'none' },
                        },
                      }}
                    >
                      {allowed ? (
                        <CardActionArea
                          component={RouterLink}
                          to={`/reports/${r.slug}`}
                          sx={{ height: '100%' }}
                        >
                          {body}
                        </CardActionArea>
                      ) : (
                        <Tooltip title="Your role doesn’t include this report">
                          <Box sx={{ height: '100%' }}>{body}</Box>
                        </Tooltip>
                      )}
                    </Card>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </>
  );
}
